<script setup lang="ts">
import { computed, ref } from 'vue'
import FileInput from './FileInput.vue'

interface OrderFile {
  uuid: string
  fileName: string
  path: string
}

const fileInputRef = ref<InstanceType<typeof FileInput> | null>(null)
const hiddenInputRef = ref<HTMLInputElement | null>(null)

const props = withDefaults(
  defineProps<{
    name?: string
    label?: string
    hideLabel?: boolean
    hideFileInput?: boolean
    addButtonLabel?: string
    required?: boolean
    disabled?: boolean
    accept?: string
    multiple?: boolean
    errorMessage?: string
    existingFiles?: OrderFile[]
  }>(),
  { existingFiles: () => [], hideLabel: false, hideFileInput: false, addButtonLabel: 'Ajouter une annexe' },
)

const emit = defineEmits<{
  change: [files: FileList | null]
  deleteFile: [uuid: string]
}>()

const suiteUrl = import.meta.env.VITE_SUITE_URL || ''
const fileUrl = (uuid: string) => `${suiteUrl}/orders/file/${uuid}`

const hasExisting = computed(() => (props.existingFiles?.length ?? 0) > 0)

function triggerFileSelect() {
  if (props.hideFileInput) {
    hiddenInputRef.value?.click()
  } else {
    const comp = fileInputRef.value as { fileInputRef?: { value?: HTMLInputElement } } | null
    comp?.fileInputRef?.value?.click()
  }
}

defineExpose({ triggerFileSelect })
</script>

<template>
  <div class="form-control w-full">
    <div v-if="label && hasExisting && !hideLabel" class="label">
      <span class="label-text font-semibold">
        {{ label }}
        <span v-if="required" class="text-red-500">*</span>
      </span>
    </div>
    <div v-if="hasExisting" class="space-y-2 mb-2">
      <div v-for="f in existingFiles" :key="f.uuid" class="flex items-center justify-between gap-2 p-2 bg-base-200 rounded border border-base-300">
        <a :href="fileUrl(f.uuid)" target="_blank" class="link link-hover text-sm truncate flex-1 min-w-0">
          {{ f.fileName }}
        </a>
        <button v-if="!disabled" type="button" class="btn btn-ghost btn-xs text-error shrink-0" @click="emit('deleteFile', f.uuid)">
          Supprimer
        </button>
      </div>
    </div>
    <div v-if="!hideFileInput">
      <FileInput
        ref="fileInputRef"
        :name="name"
        :label="hasExisting ? 'Remplacer / Ajouter' : (label ?? 'Pièces jointes')"
        :required="required && !hasExisting"
        :disabled="disabled"
        :accept="accept"
        :multiple="multiple"
        :error-message="errorMessage"
        @change="emit('change', $event)"
      />
    </div>
    <label v-else class="btn btn-outline btn-sm btn-primary gap-2 cursor-pointer">
      <input
        ref="hiddenInputRef"
        type="file"
        :name="name"
        :accept="accept"
        :multiple="multiple"
        :disabled="disabled"
        class="absolute w-0 h-0 opacity-0 overflow-hidden"
        @change="(e: Event) => emit('change', (e.target as HTMLInputElement).files)"
      />
      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
        <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
      </svg>
      {{ addButtonLabel }}
    </label>
    <div v-if="errorMessage" class="label">
      <span class="label-text-alt text-red-500">{{ errorMessage }}</span>
    </div>
  </div>
</template>
